import { ChangeDetectionStrategy, Component, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

let idCounter = 0;
const nextId = (): string => `bx-field-toggle-${++idCounter}`;

/**
 * Accessible on/off switch. Renders a native checkbox under the hood so it's
 * keyboard- and screen-reader-friendly without ARIA gymnastics. Visual switch is CSS.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-field-toggle',
  standalone: true,
  templateUrl: './field-toggle.component.html',
  styleUrls: ['./field-toggle.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldToggleComponent),
      multi: true,
    },
  ],
})
export class FieldToggleComponent implements ControlValueAccessor {
  readonly label = input.required<string>();
  readonly description = input<string | undefined>(undefined);
  readonly inputId = input<string>(nextId());

  protected readonly value = signal(false);
  protected readonly disabled = signal(false);

  private onChange: (value: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: boolean | null | undefined): void {
    this.value.set(!!value);
  }
  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected handleChange(event: Event): void {
    const next = (event.target as HTMLInputElement).checked;
    this.value.set(next);
    this.onChange(next);
    this.onTouched();
  }
}
